import {
  Paper,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Table,
  TableHead,
  TableSortLabel,
} from "@mui/material";
import momentTimezone from "moment-timezone";
import React from "react";
import { visuallyHidden } from "@mui/utils";
import DownloadClips from "../DownloadClips";
const userTimeZone = momentTimezone.tz.guess();
interface Data {
  date: string;
  away_team: string;
  home_team: string;
  name: string;
  jobStatus: string;
  button: any;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "date",
    numeric: false,
    disablePadding: true,
    label: "Date",
  },
  {
    id: "away_team",
    numeric: false,
    disablePadding: false,
    label: "Away Team",
  },
  {
    id: "home_team",
    numeric: false,
    disablePadding: false,
    label: "Home Team",
  },
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: "Type",
  },
  {
    id: "jobStatus",
    numeric: false,
    disablePadding: false,
    label: "Status",
  },
  {
    id: "button",
    numeric: false,
    disablePadding: false,
    label: "",
  },
];

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Order;
  orderBy: string;
  /*
   * False while rows are still arriving a page at a time, in which case the
   * headers are plain labels rather than sort controls. See isFullyLoaded in
   * the table below for why sorting a partial set is not offered.
   */
  sortable: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, sortable } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) =>
          headCell.label ? (
            <TableCell
              key={headCell.id}
              align={headCell.numeric ? "right" : "left"}
              sortDirection={
                sortable && orderBy === headCell.id ? order : false
              }
            >
              {sortable ? (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </TableSortLabel>
              ) : (
                headCell.label
              )}
            </TableCell>
          ) : (
            <TableCell></TableCell>
          )
        )}
      </TableRow>
    </TableHead>
  );
}
const DownloadedClipsTable = ({
  isInsider,
  clipsData,
  /*
   * How many rows exist in total, which is not always how many this table
   * holds. It defaults to what it holds, so the tenant-fed callers on the
   * Downloads tab, which pass no total, count exactly as they did before.
   * Only the admin User Download Credit page passes a real total: the layer
   * answers a fan's whole download history there while handing over one page
   * of rows at a time, and a credit audit that understates a fan's usage is
   * the one outcome that page cannot have.
   */
  total = clipsData?.length ?? 0,
  /*
   * Called when the reader moves to a view this table cannot fill from the
   * rows it holds. The owner decides whether there is anything left to fetch,
   * so calling this when there is not is harmless.
   */
  onLoadMore,
}: any) => {
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("date");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const requestMoreIfNeeded = (nextPage: number, nextRowsPerPage: number) => {
    if (!onLoadMore) {
      return;
    }
    if ((nextPage + 1) * nextRowsPerPage > (clipsData?.length ?? 0)) {
      onLoadMore();
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    requestMoreIfNeeded(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
    requestMoreIfNeeded(0, nextRowsPerPage);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - clipsData.length) : 0;

  /*
   * WHETHER THIS TABLE HOLDS EVERYTHING THERE IS.
   *
   * A caller that passes no `total` holds everything by definition, so this is
   * true for every caller on the Downloads tab and they behave exactly as they
   * always have. It is false only while the admin User Download Credit page is
   * still following the Audience Layer's cursor.
   */
  const isFullyLoaded = (clipsData?.length ?? 0) >= total;

  /*
   * SORTING A PARTIAL SET WOULD LOSE ROWS ON A FORWARD WALK.
   *
   * The sort here is by game date; the layer pages by request time, newest
   * first. Those are different orderings, so re-sorting the whole set every
   * time a page is appended reshuffles the table underneath the reader, and a
   * row that moves earlier lands on a page the admin has already stepped past.
   * On a page whose job is auditing a fan against their credit cap, a row that
   * can never be reached is not an acceptable cost for a sortable header.
   *
   * So while rows are still arriving the table renders them in the order it
   * was given, which is append-only, and the headers are plain labels. The
   * sort comes back, over the whole set, once every row is here.
   */
  const visibleRows: any = React.useMemo(() => {
    const ordered = isFullyLoaded
      ? stableSort(clipsData, getComparator(order, orderBy))
      : clipsData;
    return ordered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [clipsData, isFullyLoaded, order, orderBy, page, rowsPerPage]);

  /*
   * THE ORDER STILL CHANGES ONCE, AT THE COMPLETION BOUNDARY, so send the
   * reader back to the start of the new one.
   *
   * Holding the handed order while rows arrive removes the reshuffle on every
   * appended page, but not the last one: the instant the final page lands,
   * isFullyLoaded flips and the whole set re-sorts by game date while `page`
   * stays where the reader was standing. A row that sorts earlier than where
   * they have already walked is then behind them and never seen, which is the
   * same lost row the partial-set sort was avoiding, just once instead of
   * every time.
   *
   * Resetting the page is the honest answer: the ordering the reader was
   * walking no longer exists, so their position in it does not mean anything.
   * Better to visibly start again than to silently skip rows on the one screen
   * that audits a fan against their credit cap.
   */
  const wasFullyLoaded = React.useRef(isFullyLoaded);
  React.useEffect(() => {
    if (isFullyLoaded && !wasFullyLoaded.current) {
      setPage(0);
    }
    wasFullyLoaded.current = isFullyLoaded;
  }, [isFullyLoaded]);

  return (
    <Paper sx={{ width: "100%", mb: 2, overflow: "hidden" }}>
      <TableContainer sx={{ height: visibleRows?.length ? 440 : "auto" }}>
        <Table stickyHeader sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            sortable={isFullyLoaded}
          />
          <TableBody>
            {visibleRows.map((row: any, index: any) => {
              return (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={index + 1}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell component="th" scope="row">
                    {row?.game_details?.game_date_time &&
                      momentTimezone
                        .tz(
                          row?.game_details?.game_date_time,
                          "YYYY-MM-DD hh:mm:ss.SSSSSS a Z",
                          "America/New_York"
                        )
                        .tz(userTimeZone)
                        .format("MM/DD/YYYY")}
                  </TableCell>
                  <TableCell align="left">
                    {" "}
                    {row?.game_details?.away_team &&
                      `${row?.game_details?.away_team}`}
                  </TableCell>
                  <TableCell align="left">
                    {row?.game_details?.home_team}
                  </TableCell>

                  <TableCell align="left">{row.name}</TableCell>
                  <TableCell align="left">{row.jobStatus}</TableCell>
                  <TableCell align="right">
                    <DownloadClips isInsider={isInsider} el={row} />
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow>
                <TableCell colSpan={6} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
export default DownloadedClipsTable;
